import React, { useEffect, useRef, useState } from 'react';
import { X, Trash2, Loader2, PanelLeftClose, PanelLeftOpen, Rocket, Edit } from 'lucide-react';
import { Message, AgentType, WorkflowNode } from '../types';
import { ChatMessage } from './chat-message';
import { ChatInput } from './chat-input';
import { AgentSelection } from './agent-selection';
import { WorkflowProgress } from './workflow-progress';
import { SessionTokenBar } from './session-token-bar';
import { PromptStarters } from './prompt-starters';

interface Chat {
  _id: string;
  title?: string;
  sessionId: string;
  messages: any[];
  createdAt: string;
  updatedAt: string;
}

interface ChatWindowProps {
  isOpen: boolean;
  messages: Message[];
  isLoading: boolean;
  selectedAgent: AgentType;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  maxMessages?: number;
  currentSessionId?: string;
  availableChats?: Chat[];
  isSwitchingChat?: boolean;
  deletingChatId?: string | null;
  streamingNodes?: WorkflowNode[];
  onClose: () => void;
  onSendMessage: (message: string) => void;
  onSelectAgent: (agent: AgentType) => void;
  onRestart: () => void;
  onSwitchChat?: (sessionId: string) => void;
  onDeleteChat?: (sessionId: string) => void;
  onNewChat?: () => void;
}

export function ChatWindow({
  isOpen,
  messages,
  isLoading,
  selectedAgent,
  title = "Clickster AI Assistant",
  subtitle = "Ask me anything about your campaigns",
  placeholder = "Type your message...",
  maxMessages = 5,
  currentSessionId,
  availableChats = [],
  isSwitchingChat = false,
  deletingChatId = null,
  streamingNodes = [],
  onClose,
  onSendMessage,
  onSelectAgent,
  onRestart,
  onSwitchChat,
  onDeleteChat,
  onNewChat
}: ChatWindowProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);

  const loadingTexts = [
    "Thinking",
    "Analyzing data",
    "Understanding context",
    "Processing your request",
    "Gathering insights",
    "Formulating response",
    "Connecting the dots",
    "Reviewing campaign data"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingTextIndex((prev) => (prev + 1) % loadingTexts.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isLoading, loadingTexts.length]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarExpanded(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isLoading && !loadingStartTime) {
      setLoadingStartTime(Date.now());
    } else if (!isLoading) {
      setLoadingStartTime(null);
    }
  }, [isLoading, loadingStartTime]);

  if (!isOpen) return null;

  // Detect incomplete workflow (no DONE event received) and 30s elapsed
  const lastAssistantMsg = [...messages].reverse().find(m => m.sender === 'assistant');
  const hasDoneEvent = lastAssistantMsg?.workflowNodes?.some(
    node => node.name === 'generate_summary' && node.details?.confidence != null
  );
  const secondsElapsed = loadingStartTime ? (Date.now() - loadingStartTime) / 1000 : 0;
  const showRefreshButton = isLoading && !hasDoneEvent && streamingNodes.length > 0 && secondsElapsed >= 30;

  return (
    <div 
      className={`bg-white shadow-2xl border border-gray-200 flex transition-all duration-300 ease-in-out
        ${isMobile ? 'fixed inset-0 w-full h-full' : 'w-[45vw] h-screen max-h-screen'}`}
      data-testid="chat-window"
    >
      {/* Sidebar for chat list */}
      <div className={`${isSidebarExpanded ? (isMobile ? 'w-40' : 'w-52') : 'w-12'} border-r border-gray-200 flex flex-col bg-white transition-all duration-300`}>
        {/* Header section with app icon space and collapse button */}
        <div className={`${isSidebarExpanded ? 'p-3 pb-0' : 'p-2'} flex items-center justify-between`}>
          {isSidebarExpanded && (
            <div className="flex items-center">
              <Rocket className="w-6 h-6 text-blue-600" />
            </div>
          )}
          <button
            onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
            className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            data-testid="sidebar-toggle-button"
            aria-label={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isSidebarExpanded ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
          </button>
        </div>

        {/* New Chat button section */}
        {isSidebarExpanded && (
          <div className="p-3">
            <button
              onClick={onNewChat}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 bg-transparent hover:bg-gray-50 transition-colors"
              data-testid="new-chat-button"
            >
              <Edit className="w-4 h-4" />
              New chat
            </button>
          </div>
        )}
        {isSidebarExpanded && (
          <div className="flex-1 overflow-y-auto px-2">
            {availableChats.map((chat) => (
              <div
                key={chat.sessionId}
                className={`flex items-center justify-between w-full px-3 py-2 mb-1 text-sm rounded-md hover:bg-gray-50 transition-colors group ${chat.sessionId === currentSessionId ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
              >
                <button
                  onClick={() => onSwitchChat?.(chat.sessionId)}
                  className="flex-1 text-left"
                  data-testid={`chat-session-option-${chat.sessionId}`}
                >
                  <div className={`font-medium text-sm transition-all duration-200 ${chat.sessionId === currentSessionId ? 'text-blue-600' : 'text-gray-700'}`}>
                    {chat.title || 'New Chat'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(chat.updatedAt).toLocaleDateString()}
                  </div>
                </button>
                {onDeleteChat && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteChat(chat.sessionId);
                    }}
                    disabled={deletingChatId === chat.sessionId}
                    className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`chat-delete-button-${chat.sessionId}`}
                    aria-label="Delete chat"
                  >
                    {deletingChatId === chat.sessionId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center gap-2 justify-between px-4 py-4 border-b border-gray-200 bg-blue-600 text-white h-[73px]">
          <div className="flex-1">
            <h3 className="font-semibold" data-testid="chat-window-title">
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm text-blue-100" data-testid="chat-window-subtitle">
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
              data-testid="chat-window-close-button"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto p-4 space-y-2"
          data-testid="chat-messages-container"
        >
          {isSwitchingChat ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-500">Loading chat...</span>
              </div>
            </div>
          ) : !selectedAgent ? (
            <AgentSelection onSelectAgent={onSelectAgent} />
          ) : (
            <>
              {messages.length === 0 && (
                    <PromptStarters agentType={selectedAgent} onSelect={onSendMessage} />
              )}

              {messages.map((message, idx) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onRetry={message.isError ? () => {
                    const lastUserMsg = [...messages].reverse().find(m => m.sender === 'user');
                    if (lastUserMsg) onSendMessage(lastUserMsg.text);
                  } : undefined}
                />
              ))}

              {isLoading && (
                    <>
                      <div
                        className="flex justify-start mb-4"
                        data-testid="chat-loading-indicator"
                      >
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-4 py-2">
                          {streamingNodes.length > 0 ? (
                            <WorkflowProgress nodes={streamingNodes} isLive />
                          ) : (
                            <span
                              className="text-xs transition-all duration-1000 ease-in-out relative overflow-hidden"
                              data-testid="chat-loading-text"
                            >
                              <span
                                className="inline-block relative text-gray-700"
                                style={{
                                  background: 'linear-gradient(90deg, rgba(55, 65, 81, 1) 0%, rgba(16, 185, 129, 1) 50%, rgba(55, 65, 81, 1) 100%)',
                                  backgroundSize: '200% 100%',
                                  animation: 'shimmer 3.5s ease-in-out infinite',
                                  WebkitBackgroundClip: 'text',
                                  backgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  color: 'rgb(55, 65, 81)'
                                }}
                              >
                                {loadingTexts[loadingTextIndex]}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>

                      {showRefreshButton && (
                        <div className="flex justify-start mb-4 ml-12">
                          <button
                            onClick={() => {
                              // Reload chat history from server (same as clicking a chat in sidebar)
                              if (currentSessionId) {
                                onSwitchChat?.(currentSessionId);
                              }
                            }}
                            disabled={isSwitchingChat}
                            className="text-xs text-orange-600 hover:text-orange-700 underline cursor-pointer disabled:opacity-50"
                            data-testid="chat-refresh-button"
                          >
                            {isSwitchingChat ? 'Refreshing...' : 'This is taking a long time. Click to refresh if it seems stuck.'}
                          </button>
                        </div>
                      )}
                    </>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {selectedAgent && (
          <div className="flex border-t border-gray-200 flex-col w-full">
            <SessionTokenBar messages={messages} />
            <ChatInput
              messages={messages}
              onSendMessage={onSendMessage}
              disabled={isLoading}
              placeholder={placeholder}
            />
          </div>
        )}
      </div>
    </div>
  );
}
